import * as React from "react";

import { cn } from "@replohq/lattice/utils/cn";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

type CommerceStoryMark = "spark" | "orbit" | "path";

const commerceStoryBenefits = [
  {
    title: "Build the storefront",
    description:
      "Create pages, products, and campaigns through one conversation.",
    mark: "spark",
  },
  {
    title: "Keep the whole picture",
    description:
      "Bring brand, product, asset, and performance context into every project.",
    mark: "orbit",
  },
  {
    title: "Move from idea to live",
    description:
      "Plan the work, make the changes, and publish without stitching tools together.",
    mark: "path",
  },
] as const satisfies readonly {
  title: string;
  description: string;
  mark: CommerceStoryMark;
}[];

function CommerceStoryMarkGraphic({ mark }: { mark: CommerceStoryMark }) {
  return exhaustiveSwitch({ type: mark })({
    spark: () => (
      <svg viewBox="0 0 36 36" fill="none" aria-hidden className="size-9">
        <path
          d="M18 2.5C18 11.8 12.8 18 3.5 18C12.8 18 18 24.2 18 33.5C18 24.2 23.2 18 32.5 18C23.2 18 18 11.8 18 2.5Z"
          fill="var(--color-brand-light-pink)"
          stroke="var(--color-brand-magenta)"
          strokeWidth="1"
        />
        <circle cx="18" cy="18" r="2.5" fill="var(--color-brand-magenta)" />
      </svg>
    ),
    orbit: () => (
      <svg viewBox="0 0 36 36" fill="none" aria-hidden className="size-9">
        <path
          d="M18 5C21.5 5 23.6 9.5 23.6 18C23.6 26.5 21.5 31 18 31C14.5 31 12.4 26.5 12.4 18C12.4 9.5 14.5 5 18 5Z"
          fill="var(--color-brand-light-green)"
          stroke="var(--color-brand-dark-green)"
          strokeWidth="1"
        />
        <path
          d="M6.7 11.5C8.4 8.5 13.4 9 20.8 13.3C28.2 17.5 31 21.7 29.3 24.5C27.6 27.5 22.6 27 15.2 22.7C7.8 18.5 5 14.3 6.7 11.5Z"
          fill="var(--color-brand-seashell)"
          fillOpacity="0.72"
          stroke="var(--color-brand-dark-green)"
          strokeWidth="1"
        />
        <circle cx="18" cy="18" r="3" fill="var(--color-brand-dark-green)" />
      </svg>
    ),
    path: () => (
      <svg viewBox="0 0 36 36" fill="none" aria-hidden className="size-9">
        <path
          d="M4 27C9 10 17 31 23 15C25.5 8.5 29 7 33 8"
          stroke="var(--color-brand-purple)"
          strokeWidth="1.5"
          strokeDasharray="2.5 3"
          strokeLinecap="round"
        />
        <path
          d="M8 21C8 24.6 6.1 26.5 2.5 26.5C6.1 26.5 8 28.4 8 32C8 28.4 9.9 26.5 13.5 26.5C9.9 26.5 8 24.6 8 21Z"
          fill="var(--color-brand-yellow)"
          stroke="var(--color-brand-purple)"
          strokeWidth="1"
        />
        <path
          d="M29 2C29 5.3 27.3 7 24 7C27.3 7 29 8.7 29 12C29 8.7 30.7 7 34 7C30.7 7 29 5.3 29 2Z"
          fill="var(--color-brand-light-pink)"
          stroke="var(--color-brand-purple)"
          strokeWidth="1"
        />
      </svg>
    ),
  });
}

export function AgentCommerceStory({
  variant,
}: {
  variant: "carousel" | "modal";
}) {
  const isCarousel = variant === "carousel";

  return (
    <div
      className={cn(
        "flex flex-col",
        isCarousel ? "items-center gap-3 text-center" : "gap-6",
      )}
    >
      <div
        className={cn(
          "flex flex-col gap-2",
          isCarousel && "items-center text-center",
        )}
      >
        <h2
          className={cn(
            "font-editorial text-3xl leading-tight",
            isCarousel ? "text-brand-seashell" : "italic text-default",
          )}
        >
          Run your entire business from one place
        </h2>
        <p
          className={cn(
            "leading-relaxed",
            isCarousel
              ? "max-w-[430px] text-sm text-brand-seashell/65"
              : "text-base text-muted",
          )}
        >
          From first idea to published storefront, turn your brand, products,
          campaigns, and store data into finished work.
        </p>
      </div>

      {!isCarousel && (
        <div className="relative flex flex-col gap-4">
          <svg
            viewBox="0 0 32 160"
            preserveAspectRatio="none"
            aria-hidden
            className="absolute bottom-5 left-0 top-5 w-9 text-muted/15"
          >
            <path
              d="M18 0C4 30 30 46 17 78C5 108 27 128 18 160"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.25"
              strokeDasharray="2 6"
              strokeLinecap="round"
            />
          </svg>
          {commerceStoryBenefits.map((benefit) => (
            <div
              key={benefit.title}
              className="relative flex items-start gap-3"
            >
              <span className="relative z-10 flex size-9 shrink-0 items-center justify-center bg-sheet">
                <CommerceStoryMarkGraphic mark={benefit.mark} />
              </span>
              <span className="flex flex-col gap-0.5 pt-0.5">
                <span className="text-base font-semibold leading-snug text-default">
                  {benefit.title}
                </span>
                <span className="text-sm leading-relaxed text-muted">
                  {benefit.description}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
